import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/stores/app-store';
import { REFRESH_INTERVALS } from '@/config/data-sources';

import { fetchEarthquakes } from '@/services/usgs';
import { fetchGdeltEvents, fetchGdeltNews } from '@/services/gdelt';
import { fetchWildfires } from '@/services/nasa-firms';
import { fetchAllFeeds } from '@/services/rss';
import { fetchMarketData } from '@/services/market';
import { fetchAircraft } from '@/services/opensky';
import { startAisStream, stopAisStream, HAS_AIS } from '@/services/ais-stream';
import { fetchTrafficCameras } from '@/services/traffic-cameras';
import { fetchShipData, fetchNavalVessels } from '@/services/ship-tracker';
import { fetchPowerPlants } from '@/services/power-plants';
import { fetchConflictZones, fetchFighterJets, fetchDroneZones, fetchNuclearAssets } from '@/services/conflict-zones-export';
import { fetchSubmarineCables } from '@/services/submarine-cables';
import {
  fetchForexRates,
  fetchSyntheticCommodities,
  fetchSyntheticCommodityMetals,
  fetchSyntheticDefenseMarket,
  fetchSyntheticFoodMarket,
  fetchSyntheticMacro,
} from '@/services/forex';
import { fetchStockQuotes, fetchSyntheticBonds } from '@/services/stocks';
import { detectCrises } from '@/algorithms/crisis-detector';
import { detectBreakingAlerts } from '@/algorithms/breaking-alert';
import { TROOP_MOVEMENTS } from '@/services/troop-movements';
import { tickDrones } from '@/services/drone-attacks';
import { fetchCyberThreats } from '@/services/cyber-threats';
import { fetchSocialSignals } from '@/services/social-signals';
import { BOND_YIELDS, CREDIT_SPREADS, fetchLiveCalendar } from '@/services/economic-calendar';
import { fetchSanctions } from '@/services/sanctions';
import { fetchWorldCities } from '@/services/world-cities';
import { fetchCountryProfiles } from '@/services/country-profiles';
import { fetchRestCountries } from '@/services/rest-countries';
import { fetchChokepoints, fetchCarrierGroups } from '@/services/strategic-assets';
import { fetchCountryRivalries } from '@/services/country-conflicts';
import { fetchAcledEvents } from '@/services/acled';
import { fetchNotams } from '@/services/notam';
import { fetchMilitaryAircraft } from '@/services/military-aircraft';
import { fetchTelegramSignals } from '@/services/telegram';
import { fetchLiveUAMapEvents } from '@/services/liveuamap';
import { computeAllCII } from '@/algorithms/country-instability';

import type { GeoEvent, NewsItem, MarketQuote, CountryRisk } from '@/types';

// ---------------------------------------------------------------------------
// localStorage cache helpers — stale-while-revalidate for news + risk scores
// ---------------------------------------------------------------------------
const NEWS_CACHE_KEY = 'dx-news-v1';
const RISKS_CACHE_KEY = 'dx-risks-v1';
const CACHE_TTL = 5 * 60 * 1000; // 5 min (↓ from 20 min)

// Asia source names (module-level so cache init can reuse)
const ASIA_SOURCES = new Set([
  'Yonhap (EN)', 'JoongAng Daily', 'Korea Herald', 'Hankyoreh (EN)',
  '조선일보', '한겨레', '연합뉴스', '경향신문', '동아일보', '매일경제',
  'NHK World', 'Japan Times', 'Mainichi', 'SCMP', 'Global Times',
  'Focus Taiwan', 'Taiwan News', 'CNA Singapore', 'Bangkok Post',
  'Straits Times', 'VnExpress', 'Jakarta Post', 'Rappler',
  'Times of India', 'Dawn (Pakistan)', 'ABC Australia', 'Nikkei Asia',
]);

function readCache<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const { ts, data } = JSON.parse(raw) as { ts: number; data: T };
    if (Date.now() - ts > CACHE_TTL) return undefined;
    return data;
  } catch { return undefined; }
}

function writeCache<T>(key: string, data: T): void {
  try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch {}
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const QUERY_KEYS = {
  earthquakes: ['earthquakes'] as const,
  gdeltEvents: ['gdelt', 'events'] as const,
  gdeltNews: ['gdelt', 'news'] as const,
  wildfires: ['wildfires'] as const,
  rssNews: ['rss', 'news'] as const,
  allMarket: ['all-market'] as const,
  aircraft: ['aircraft'] as const,
  ships: ['ships'] as const,
  naval: ['naval'] as const,
  fighters: ['fighters'] as const,
  powerPlants: ['power-plants'] as const,
  conflictZones: ['conflict-zones'] as const,
  droneZones: ['drone-zones'] as const,
  nuclearAssets: ['nuclear-assets'] as const,
  submarineCables: ['submarine-cables'] as const,
  acledEvents: ['acled', 'events'] as const,
  notams: ['notam'] as const,
  militaryAircraft: ['military-aircraft'] as const,
  telegramSignals: ['telegram', 'signals'] as const,
  liveuamap: ['liveuamap', 'events'] as const,
} as const;

// ---------------------------------------------------------------------------
// Data Orchestrator Hook
// ---------------------------------------------------------------------------

export interface DataOrchestratorResult {
  isLoading: boolean;
  errors: Record<string, Error | null>;
  refetchAll: () => void;
}

export function useDataOrchestrator(): DataOrchestratorResult {
  const queryClient = useQueryClient();
  const {
    setEarthquakes, setEvents, setNews, setAsiaNews, setWildfires, updateFreshness,
    setAircraft, setShips, setNavalVessels, setFighterJets, setPowerPlants,
    setConflictZones, setDroneZones, setNuclearAssets, setSubmarineCables,
    setMarketQuotes, setCrisisAlerts, setTroopMovements,
    setCyberThreats, setSocialSignals, setEconomicCalendar, setBondYields, setCreditSpreads, setSanctions,
    setDroneAttacks,
    setWorldCities, setCountryProfiles, setAllCountries, setChokepoints, setCarrierGroups, setCountryRivalries,
    setAcledEvents, setNotams, setMilitaryAircraft, setTrafficCameras,
    setCountryRisks, addBreakingAlerts,
  } = useAppStore();

  const storeRef = useRef(useAppStore.getState());
  useEffect(() => {
    return useAppStore.subscribe((state) => { storeRef.current = state; });
  }, []);

  // --- Restore from localStorage on mount (stale-while-revalidate) ----------
  useEffect(() => {
    const cachedNews = readCache<NewsItem[]>(NEWS_CACHE_KEY);
    if (cachedNews?.length) {
      setNews(cachedNews);
      setAsiaNews(cachedNews.filter((item) => ASIA_SOURCES.has(item.source)));
    }
    const cachedRisks = readCache<CountryRisk[]>(RISKS_CACHE_KEY);
    if (cachedRisks?.length) {
      setCountryRisks(cachedRisks);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Earthquakes ----------------------------------------------------------
  const earthquakesQuery = useQuery({
    queryKey: QUERY_KEYS.earthquakes,
    queryFn: fetchEarthquakes,
    refetchInterval: REFRESH_INTERVALS.EARTHQUAKES,
    staleTime: REFRESH_INTERVALS.EARTHQUAKES / 2,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });
  useEffect(() => {
    if (earthquakesQuery.data) { setEarthquakes(earthquakesQuery.data); updateFreshness('earthquakes', 'fresh'); }
    if (earthquakesQuery.isLoading) updateFreshness('earthquakes', 'loading');
    if (earthquakesQuery.isError) updateFreshness('earthquakes', 'error');
  }, [earthquakesQuery.data, earthquakesQuery.isLoading, earthquakesQuery.isError, setEarthquakes, updateFreshness]);

  // --- GDELT Events ---------------------------------------------------------
  // NOTE: GDELT GEO API deprecated → fetchGdeltEvents()는 항상 []를 반환.
  // 구 'conflicts'/'news' 지도 레이어는 제거됨 (실제 분쟁 이벤트는 acledEvents가 담당).
  // 이 쿼리는 store.events → CII(computeAllCII) / crisis-detector 파이프라인이
  // 참조하므로 유지. 대체 이벤트 소스 연결 시 이 경로를 재사용할 것.
  const gdeltEventsQuery = useQuery({
    queryKey: QUERY_KEYS.gdeltEvents,
    queryFn: fetchGdeltEvents,
    refetchInterval: REFRESH_INTERVALS.GDELT,
    staleTime: REFRESH_INTERVALS.GDELT / 2,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });
  useEffect(() => {
    if (gdeltEventsQuery.data) { setEvents(gdeltEventsQuery.data); updateFreshness('gdelt-events', 'fresh'); }
    if (gdeltEventsQuery.isLoading) updateFreshness('gdelt-events', 'loading');
    if (gdeltEventsQuery.isError) updateFreshness('gdelt-events', 'error');
  }, [gdeltEventsQuery.data, gdeltEventsQuery.isLoading, gdeltEventsQuery.isError, setEvents, updateFreshness]);

  // --- GDELT News -----------------------------------------------------------
  const gdeltNewsQuery = useQuery({
    queryKey: QUERY_KEYS.gdeltNews,
    queryFn: fetchGdeltNews,
    refetchInterval: REFRESH_INTERVALS.GDELT,
    staleTime: REFRESH_INTERVALS.GDELT / 2,
    retry: 2,
  });

  // --- RSS News -------------------------------------------------------------
  const rssNewsQuery = useQuery({
    queryKey: QUERY_KEYS.rssNews,
    queryFn: () => fetchAllFeeds(),
    refetchInterval: REFRESH_INTERVALS.NEWS,
    staleTime: REFRESH_INTERVALS.NEWS / 2,
    retry: 1,
    retryDelay: 3_000,
  });

  useEffect(() => {
    const gdeltItems: NewsItem[] = gdeltNewsQuery.data ?? [];
    const rssItems: NewsItem[] = rssNewsQuery.data ?? [];
    if (!gdeltItems.length && !rssItems.length) return;
    const merged = mergeAndDeduplicateNews(gdeltItems, rssItems);
    setNews(merged);
    writeCache(NEWS_CACHE_KEY, merged.slice(0, 120));
    if (gdeltNewsQuery.data || rssNewsQuery.data) updateFreshness('news', 'fresh');
  }, [gdeltNewsQuery.data, rssNewsQuery.data, setNews, updateFreshness]);

  useEffect(() => {
    const loading = gdeltNewsQuery.isLoading || rssNewsQuery.isLoading;
    const error = gdeltNewsQuery.isError && rssNewsQuery.isError;
    if (loading) updateFreshness('news', 'loading');
    if (error) updateFreshness('news', 'error');
  }, [gdeltNewsQuery.isLoading, gdeltNewsQuery.isError, rssNewsQuery.isLoading, rssNewsQuery.isError, updateFreshness]);

  useEffect(() => {
    const rssItems: NewsItem[] = rssNewsQuery.data ?? [];
    if (!rssItems.length) return;
    setAsiaNews(rssItems.filter((item) => ASIA_SOURCES.has(item.source)));
  }, [rssNewsQuery.data, setAsiaNews]);

  // --- Wildfires ------------------------------------------------------------
  const wildfiresQuery = useQuery({
    queryKey: QUERY_KEYS.wildfires,
    queryFn: fetchWildfires,
    refetchInterval: REFRESH_INTERVALS.WILDFIRES,
    staleTime: REFRESH_INTERVALS.WILDFIRES / 2,
    retry: 2,
  });
  useEffect(() => {
    if (wildfiresQuery.data) { setWildfires(wildfiresQuery.data); updateFreshness('wildfires', 'fresh'); }
    if (wildfiresQuery.isLoading) updateFreshness('wildfires', 'loading');
    if (wildfiresQuery.isError) updateFreshness('wildfires', 'error');
  }, [wildfiresQuery.data, wildfiresQuery.isLoading, wildfiresQuery.isError, setWildfires, updateFreshness]);

  // --- Full Market Data (aggregated) ----------------------------------------
  const allMarketQuery = useQuery({
    queryKey: QUERY_KEYS.allMarket,
    queryFn: async (): Promise<MarketQuote[]> => {
      const STOCKS_IDX = 7; // fetchStockQuotes 위치 — 실데이터 우선 병합용
      const results = await Promise.allSettled([
        fetchMarketData(),
        fetchForexRates(),
        fetchSyntheticCommodities(),
        fetchSyntheticCommodityMetals(),
        fetchSyntheticDefenseMarket(),
        fetchSyntheticFoodMarket(),
        fetchSyntheticMacro(),
        fetchStockQuotes(),
        Promise.resolve(fetchSyntheticBonds()),
      ]);
      // 실시간 시세(Yahoo)와 심볼이 겹치는 synthetic 항목은 제거 — 실데이터가 대체
      const stocksResult = results[STOCKS_IDX];
      const realSymbols = new Set(
        stocksResult.status === 'fulfilled' ? stocksResult.value.map((q) => q.symbol) : [],
      );
      const quotes: MarketQuote[] = [];
      results.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        if (i === STOCKS_IDX) quotes.push(...r.value);
        else quotes.push(...r.value.filter((q) => !realSymbols.has(q.symbol)));
      });
      return quotes;
    },
    refetchInterval: REFRESH_INTERVALS.MARKET,
    staleTime: REFRESH_INTERVALS.MARKET / 2,
    retry: 2,
  });
  useEffect(() => {
    if (allMarketQuery.data) { setMarketQuotes(allMarketQuery.data); updateFreshness('market', 'fresh'); }
    if (allMarketQuery.isLoading) updateFreshness('market', 'loading');
    if (allMarketQuery.isError) updateFreshness('market', 'error');
  }, [allMarketQuery.data, allMarketQuery.isLoading, allMarketQuery.isError, setMarketQuotes, updateFreshness]);

  // --- Aircraft (OpenSky) ---------------------------------------------------
  const aircraftQuery = useQuery({
    queryKey: QUERY_KEYS.aircraft,
    queryFn: fetchAircraft,
    refetchInterval: REFRESH_INTERVALS.AIRCRAFT,
    staleTime: REFRESH_INTERVALS.AIRCRAFT / 2,
    retry: 1,
  });
  useEffect(() => {
    if (aircraftQuery.data) { setAircraft(aircraftQuery.data); updateFreshness('aircraft', 'fresh'); }
    if (aircraftQuery.isLoading) updateFreshness('aircraft', 'loading');
    if (aircraftQuery.isError) updateFreshness('aircraft', 'error');
  }, [aircraftQuery.data, aircraftQuery.isLoading, aircraftQuery.isError, setAircraft, updateFreshness]);

  // --- Ships ----------------------------------------------------------------
  // VITE_AISSTREAM_KEY 가 있으면 실시간 AIS 가 선박을 공급하므로
  // 시뮬레이션 항로 쿼리는 아예 돌리지 않는다 (가짜 데이터 노출 방지).
  const shipsQuery = useQuery({
    queryKey: QUERY_KEYS.ships,
    queryFn: async () => fetchShipData(),
    refetchInterval: REFRESH_INTERVALS.SHIPS,
    staleTime: REFRESH_INTERVALS.SHIPS / 2,
    enabled: !HAS_AIS,
  });
  useEffect(() => {
    if (!HAS_AIS && shipsQuery.data) setShips(shipsQuery.data);
  }, [shipsQuery.data, setShips]);

  // --- 전 세계 실시간 교통 CCTV ---------------------------------------------
  // 카메라 목록은 잘 바뀌지 않는다(15분). 프레임 갱신은 플레이어가 담당한다.
  const cctvQuery = useQuery({
    queryKey: ['traffic-cctv'] as const,
    queryFn: fetchTrafficCameras,
    refetchInterval: 15 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  useEffect(() => {
    if (cctvQuery.data) { setTrafficCameras(cctvQuery.data); updateFreshness('cctv', 'fresh'); }
  }, [cctvQuery.data, setTrafficCameras, updateFreshness]);

  // --- Live AIS (aisstream.io WebSocket) ------------------------------------
  useEffect(() => {
    const started = startAisStream((ships) => {
      setShips(ships);
      updateFreshness('ships', 'fresh');
    });
    if (!started) return;
    updateFreshness('ships', 'loading');
    return () => stopAisStream();
  }, [setShips, updateFreshness]);

  // --- Naval Vessels --------------------------------------------------------
  const navalQuery = useQuery({
    queryKey: QUERY_KEYS.naval,
    queryFn: async () => fetchNavalVessels(),
    refetchInterval: REFRESH_INTERVALS.NAVAL,
    staleTime: REFRESH_INTERVALS.NAVAL / 2,
  });
  useEffect(() => { if (navalQuery.data) setNavalVessels(navalQuery.data); }, [navalQuery.data, setNavalVessels]);

  // --- Fighter Jets ---------------------------------------------------------
  const fightersQuery = useQuery({
    queryKey: QUERY_KEYS.fighters,
    queryFn: async () => fetchFighterJets(),
    refetchInterval: REFRESH_INTERVALS.FIGHTERS,
    staleTime: REFRESH_INTERVALS.FIGHTERS / 2,
  });
  useEffect(() => { if (fightersQuery.data) setFighterJets(fightersQuery.data); }, [fightersQuery.data, setFighterJets]);

  // --- Power Plants ---------------------------------------------------------
  const powerPlantsQuery = useQuery({
    queryKey: QUERY_KEYS.powerPlants,
    queryFn: async () => fetchPowerPlants(),
    refetchInterval: REFRESH_INTERVALS.POWER_PLANTS,
    staleTime: REFRESH_INTERVALS.POWER_PLANTS / 2,
  });
  useEffect(() => { if (powerPlantsQuery.data) setPowerPlants(powerPlantsQuery.data); }, [powerPlantsQuery.data, setPowerPlants]);

  // --- Conflict Zones -------------------------------------------------------
  const conflictZonesQuery = useQuery({
    queryKey: QUERY_KEYS.conflictZones,
    queryFn: async () => fetchConflictZones(),
    refetchInterval: REFRESH_INTERVALS.CONFLICT_ZONES,
    staleTime: REFRESH_INTERVALS.CONFLICT_ZONES / 2,
  });
  useEffect(() => { if (conflictZonesQuery.data) setConflictZones(conflictZonesQuery.data); }, [conflictZonesQuery.data, setConflictZones]);

  // --- Drone Zones ----------------------------------------------------------
  const droneZonesQuery = useQuery({
    queryKey: QUERY_KEYS.droneZones,
    queryFn: async () => fetchDroneZones(),
    refetchInterval: REFRESH_INTERVALS.CONFLICT_ZONES,
    staleTime: REFRESH_INTERVALS.CONFLICT_ZONES / 2,
  });
  useEffect(() => { if (droneZonesQuery.data) setDroneZones(droneZonesQuery.data); }, [droneZonesQuery.data, setDroneZones]);

  // --- Nuclear Assets -------------------------------------------------------
  const nuclearQuery = useQuery({
    queryKey: QUERY_KEYS.nuclearAssets,
    queryFn: async () => fetchNuclearAssets(),
    refetchInterval: REFRESH_INTERVALS.POWER_PLANTS,
    staleTime: REFRESH_INTERVALS.POWER_PLANTS / 2,
  });
  useEffect(() => { if (nuclearQuery.data) setNuclearAssets(nuclearQuery.data); }, [nuclearQuery.data, setNuclearAssets]);

  // --- Submarine Cables (static) -------------------------------------------
  const cablesQuery = useQuery({
    queryKey: QUERY_KEYS.submarineCables,
    queryFn: async () => fetchSubmarineCables(),
    refetchInterval: REFRESH_INTERVALS.POWER_PLANTS,
    staleTime: REFRESH_INTERVALS.POWER_PLANTS / 2,
  });
  useEffect(() => { if (cablesQuery.data) setSubmarineCables(cablesQuery.data); }, [cablesQuery.data, setSubmarineCables]);

  // --- Troop Movements (static OSINT data) ----------------------------------
  useEffect(() => {
    setTroopMovements(TROOP_MOVEMENTS);
  }, [setTroopMovements]);

  // --- Drone Attacks — tick every 500ms to animate progress bars/status -----
  useEffect(() => {
    setDroneAttacks(tickDrones(Date.now()));
    const id = setInterval(() => setDroneAttacks(tickDrones(Date.now())), 500);
    return () => clearInterval(id);
  }, [setDroneAttacks]);

  // --- Telegram OSINT signals (60s refresh via t.me/s/ scraping) -----------
  const telegramQuery = useQuery({
    queryKey: QUERY_KEYS.telegramSignals,
    queryFn: fetchTelegramSignals,
    refetchInterval: 60_000,       // 60s (↓ from 3 min)
    staleTime: 30_000,
    retry: 1,
  });

  // --- Social Signals baseline (2-min refresh so timestamps stay current) --
  const socialBaseQuery = useQuery({
    queryKey: ['social-signals-base'],
    queryFn: fetchSocialSignals,
    refetchInterval: 2 * 60_000,   // 2 min (↓ from 5 min)
    staleTime: 60_000,
  });

  useEffect(() => {
    const base = socialBaseQuery.data ?? fetchSocialSignals();
    const live = telegramQuery.data ?? [];
    setSocialSignals(live.length > 0 ? [...live, ...base] : base);
  }, [socialBaseQuery.data, telegramQuery.data, setSocialSignals]);

  // --- Intelligence Data (static / low refresh) ----------------------------
  useEffect(() => {
    setCyberThreats(fetchCyberThreats());
    // setSocialSignals is handled by telegramQuery effect (with static fallback)
    setBondYields(BOND_YIELDS);
    setCreditSpreads(CREDIT_SPREADS);
    setSanctions(fetchSanctions());
  }, [setCyberThreats, setBondYields, setCreditSpreads, setSanctions]);

  // --- Economic Calendar (live — ForexFactory 주간 JSON, 30분 갱신) ----------
  const econCalQuery = useQuery({
    queryKey: ['econ-calendar', 'live'],
    queryFn: fetchLiveCalendar,
    refetchInterval: 30 * 60_000,
    staleTime: 15 * 60_000,
    retry: 1,
  });
  useEffect(() => {
    if (econCalQuery.data?.length) setEconomicCalendar(econCalQuery.data);
  }, [econCalQuery.data, setEconomicCalendar]);

  // --- Country Intelligence (static) ---------------------------------------
  useEffect(() => {
    setWorldCities(fetchWorldCities());
    const staticProfiles = fetchCountryProfiles();
    setCountryProfiles(staticProfiles);
    setAllCountries(staticProfiles); // initial: 35 static countries
    setChokepoints(fetchChokepoints());
    setCarrierGroups(fetchCarrierGroups());
    setCountryRivalries(fetchCountryRivalries());
    // Async: fetch all 195 countries from REST Countries API, merge with static
    fetchRestCountries().then((restMap) => {
      if (restMap.size === 0) return; // network fail — keep static only
      const staticCodes = new Set(staticProfiles.map((p) => p.code));
      const merged = [
        ...staticProfiles,
        ...Array.from(restMap.values()).filter((p) => !staticCodes.has(p.code)),
      ];
      setAllCountries(merged);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setWorldCities, setCountryProfiles, setAllCountries, setChokepoints, setCarrierGroups, setCountryRivalries]);

  // --- CII (Country Instability Index) — reactive + 5 min refresh ----------
  // Debounced: if GDELT events + RSS news arrive within 1.5s of each other,
  // only compute once instead of twice.
  const ciiTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(ciiTimerRef.current);
    ciiTimerRef.current = setTimeout(() => {
      const evts = storeRef.current.events;
      const newsList = storeRef.current.news;
      if (evts.length > 0 || newsList.length > 0) {
        const risks = computeAllCII(evts, newsList);
        setCountryRisks(risks);
        writeCache(RISKS_CACHE_KEY, risks);
      }
    }, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gdeltEventsQuery.data, rssNewsQuery.data, setCountryRisks]);

  // Periodic refresh every 3 min
  useEffect(() => {
    const interval = setInterval(() => {
      const risks = computeAllCII(storeRef.current.events, storeRef.current.news);
      setCountryRisks(risks);
      writeCache(RISKS_CACHE_KEY, risks);
    }, 3 * 60_000); // 3 min (↓ from 5 min)
    return () => clearInterval(interval);
  }, [setCountryRisks]);

  // --- LiveUAMap events (8-min refresh — 200 req/day hard limit) ------------
  const liveuamapQuery = useQuery({
    queryKey: QUERY_KEYS.liveuamap,
    queryFn: fetchLiveUAMapEvents,
    refetchInterval: 8 * 60_000,   // 8 min — rate limit: 200 req/day (can't reduce)
    staleTime: 4 * 60_000,
    retry: 1,
  });
  useEffect(() => {
    if (liveuamapQuery.data?.length) {
      // Merge LiveUAMap events with existing ACLED events (prepend — LiveUAMap is more real-time)
      const luaEvents = liveuamapQuery.data;
      const luaIds = new Set(luaEvents.map((e) => e.id));
      const existing = storeRef.current.acledEvents.filter((e) => !luaIds.has(e.id));
      setAcledEvents([...luaEvents, ...existing].slice(0, 500));
      updateFreshness('acled', 'fresh');
    }
  }, [liveuamapQuery.data, setAcledEvents, updateFreshness]);

  // --- ACLED Conflict Events (5-min refresh via server middleware) ----------
  const acledQuery = useQuery({
    queryKey: QUERY_KEYS.acledEvents,
    queryFn: () => fetchAcledEvents(30),
    refetchInterval: 5 * 60_000,    // 5 min (↓ from 30 min)
    staleTime: 2 * 60_000,
    retry: 1,
  });
  useEffect(() => {
    if (acledQuery.data) { setAcledEvents(acledQuery.data); updateFreshness('acled', 'fresh'); }
    if (acledQuery.isLoading) updateFreshness('acled', 'loading');
    if (acledQuery.isError) updateFreshness('acled', 'error');
  }, [acledQuery.data, acledQuery.isLoading, acledQuery.isError, setAcledEvents, updateFreshness]);

  // --- NOTAM (15-min refresh via server middleware) -------------------------
  const notamQuery = useQuery({
    queryKey: QUERY_KEYS.notams,
    queryFn: fetchNotams,
    refetchInterval: 15 * 60_000,   // 15 min (↓ from 1 hour)
    staleTime: 7 * 60_000,
    retry: 1,
  });
  useEffect(() => {
    if (notamQuery.data) { setNotams(notamQuery.data); updateFreshness('notam', 'fresh'); }
    if (notamQuery.isLoading) updateFreshness('notam', 'loading');
    if (notamQuery.isError) updateFreshness('notam', 'error');
  }, [notamQuery.data, notamQuery.isLoading, notamQuery.isError, setNotams, updateFreshness]);

  // --- Military Aircraft (adsbexchange, 30s refresh) -----------------------
  const milAircraftQuery = useQuery({
    queryKey: QUERY_KEYS.militaryAircraft,
    queryFn: fetchMilitaryAircraft,
    refetchInterval: 30_000,        // 30s (↓ from 90s — server cache 30s)
    staleTime: 20_000,
    retry: 1,
  });
  useEffect(() => {
    if (milAircraftQuery.data) { setMilitaryAircraft(milAircraftQuery.data); updateFreshness('military-aircraft', 'fresh'); }
  }, [milAircraftQuery.data, setMilitaryAircraft, updateFreshness]);

  // --- Crisis Detection — reactive (on news arrival) + periodic ------------
  // Reactive: run detection as soon as news arrives instead of waiting for timer
  useEffect(() => {
    const nw = storeRef.current.news;
    if (nw.length > 0) {
      const { events: ev, crisisAlerts: existing } = storeRef.current;
      const { alerts } = detectCrises(ev, nw, existing);
      setCrisisAlerts(alerts);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rssNewsQuery.data, gdeltNewsQuery.data, setCrisisAlerts]);

  // Periodic refresh
  useEffect(() => {
    const interval = setInterval(() => {
      const { events: ev, news: nw, crisisAlerts: existing } = storeRef.current;
      const { alerts } = detectCrises(ev, nw, existing);
      setCrisisAlerts(alerts);
    }, REFRESH_INTERVALS.CRISIS_DETECT);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCrisisAlerts]);

  // --- Breaking Alert Detection (X/Telegram — runs on every social signal update) ---
  // Reactive: triggered immediately when Telegram or social signals update
  useEffect(() => {
    const { socialSignals, news, breakingAlerts: existing } = storeRef.current;
    if (socialSignals.length === 0 && news.length === 0) return;
    const newAlerts = detectBreakingAlerts(socialSignals, news, existing);
    if (newAlerts.length > 0) addBreakingAlerts(newAlerts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramQuery.data, rssNewsQuery.data, addBreakingAlerts]);

  // --- Refetch all ----------------------------------------------------------
  const refetchAll = useCallback(() => {
    Object.values(QUERY_KEYS).forEach((key) => {
      queryClient.invalidateQueries({ queryKey: key as readonly string[] });
    });
  }, [queryClient]);

  const isLoading =
    earthquakesQuery.isLoading ||
    gdeltEventsQuery.isLoading ||
    wildfiresQuery.isLoading ||
    rssNewsQuery.isLoading;

  const errors: Record<string, Error | null> = {
    earthquakes: earthquakesQuery.error,
    gdeltEvents: gdeltEventsQuery.error,
    wildfires: wildfiresQuery.error,
    rssNews: rssNewsQuery.error,
    market: allMarketQuery.error,
    aircraft: aircraftQuery.error,
  };

  return { isLoading, errors, refetchAll };
}

// ---------------------------------------------------------------------------
// News merging helper
// ---------------------------------------------------------------------------

function mergeAndDeduplicateNews(gdeltItems: NewsItem[], rssItems: NewsItem[]): NewsItem[] {
  const all = [...rssItems, ...gdeltItems];
  all.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  const seen = new Set<string>();
  const result: NewsItem[] = [];
  for (const item of all) {
    const key = normalizeTitle(item.title);
    if (key && !seen.has(key)) { seen.add(key); result.push(item); }
  }
  // 150으로 확대 — defense 피드 22개가 일반 뉴스와 경쟁해도 military 탭 깊이 확보
  return result.slice(0, 150);
}

function normalizeTitle(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim().slice(0, 60);
}
